/**
 * /checkout/plan — Payment page.
 *
 * The rider has already reviewed their plan in the wizard, so this page
 * only carries the two things they still need to act on:
 *   • Price breakdown (cost, discounts, total)
 *   • Payment (Stripe Elements + Apple/Google Pay wallets)
 * A single collapsed "Have a promo code?" link sits above the wallet.
 * Stripe integration is untouched.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MapFrostBackdrop from "@/shared/components/MapFrostBackdrop";
import { toast } from "sonner";
import { ArrowLeft, Check, Tag, Sparkles, X } from "lucide-react";

import { api, formatApiError } from "@/shared/lib/api";
import { formatWindow as formatDrumWindow } from "@/apps/rider/components/booking/TimeDrum";
import StripeCheckoutSheet from "@/shared/components/StripeCheckoutSheet";

const DAY_FULL = { Mon: "Mon", Tue: "Tue", Wed: "Wed", Thu: "Thu", Fri: "Fri", Sat: "Sat", Sun: "Sun" };
const DAY_ORDER_KEY = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
const PLAN_STASH_KEY = "link.checkout.pendingPlan";

// One-line reassurance folded under the total — replaces the standalone
// "What happens next" card. Covers cancellation, no-surge, edit-anytime,
// and the round-trip nature of the plan in body text without an icon or
// header so it doesn't compete with the CTA.
const REASSURANCE_COPY =
  "Round-trip · no surge · free cancellation up to 2 hours before pickup · edit your plan anytime.";

export default function CheckoutPlan() {
  const location = useLocation();
  const nav = useNavigate();

  const plan = useMemo(() => {
    const fromState = location.state?.plan;
    if (fromState) {
      try { sessionStorage.setItem(PLAN_STASH_KEY, JSON.stringify(fromState)); } catch (_) { /* noop */ }
      return fromState;
    }
    try {
      const raw = sessionStorage.getItem(PLAN_STASH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  }, [location.state]);

  const [creating, setCreating] = useState(false);
  const [routeId, setRouteId] = useState(plan?.routeId || null);

  // Pricing state — populated from the Stripe sheet's intent fetch via
  // onIntentReady so the breakdown rendered above the payment exactly
  // matches what the rider will be charged. No hidden math.
  const [intent, setIntent] = useState(null);
  const [promoCode, setPromoCode] = useState(null);

  // Inline error state — used when the intent fetch fails or Stripe.js
  // can't load. Previously we redirected to /api/pay/hosted (which doesn't
  // exist on the backend → 404 → generic platform error page). Now we
  // show a structured, rider-facing message + actionable next steps.
  const [intentError, setIntentError] = useState(null);

  useEffect(() => {
    if (!plan) {
      toast.error("Start a new transportation plan from the map.");
      nav("/", { replace: true });
    }
  }, [plan, nav]);

  // Auto-create the route doc. Fires on mount so the network round-trip
  // happens while the rider is reviewing the summary above.
  useEffect(() => {
    if (!plan || routeId) return;
    let cancelled = false;
    (async () => {
      setCreating(true);
      try {
        const { data: route } = await api.post("/routes", {
          pickup_address: plan.pickup,
          destination_address: plan.destination,
          days: plan.days,
          arrival_time: plan.arrival,
          return_time: plan.ret,
          buffer_minutes: Number(plan.buffer),
          distance_miles: Number(plan.distance),
          billing_cycle: plan.billingCycle,
          day_overrides: plan.dayOverrides || {},
          seats_needed: Number(plan.seats || 1),
          transport_preference: plan.pref || "shared",
        });
        if (cancelled) return;
        setRouteId(route.id);
      } catch (e) {
        if (cancelled) return;
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
        nav("/", { replace: true });
      } finally {
        if (!cancelled) setCreating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [plan, routeId, nav]);

  if (!plan) return null;

  const sortedDays = (plan.days || []).slice().sort((a, b) => (DAY_ORDER_KEY[a] ?? 9) - (DAY_ORDER_KEY[b] ?? 9));
  const dayStr = sortedDays.map((d) => DAY_FULL[d] || d).join(" • ");
  // Preserved for the /checkout/success confirmation payload — the
  // wizard-side review already showed these to the rider.
  const pickupLabel = plan.pickupWindow ? formatDrumWindow(plan.pickupWindow) : "—";
  const returnLabel = plan.returnWindow ? formatDrumWindow(plan.returnWindow) : "—";
  const seatsLabel = (plan.seats || 1) >= 5 ? "5+" : String(plan.seats || 1);
  const prefLabel = plan.pref === "private" ? "Private ride" : "Shared ride";
  const dailyRate = Number(plan.dailyRate || 0);
  const numDays = sortedDays.length || 1;
  const isWeekly = plan.billingCycle === "weekly";

  // Pricing — prefer backend intent (source of truth) so every line
  // reconciles to the exact amount Stripe will charge.
  const baseAmount = Number(intent?.base_amount ?? dailyRate * numDays);
  const finalAmount = Number(intent?.amount ?? baseAmount);
  const totalDiscount = Math.max(0, +(baseAmount - finalAmount).toFixed(2));
  const promoApplied = intent?.promo?.applied
    ? { code: intent.promo.label || promoCode, savings: Number(intent.promo.savings || 0) }
    : null;
  // First-ride / welcome discounts are server-applied (no code required).
  // We surface them as an explicit line item — and as a celebratory badge —
  // so the rider sees exactly why their total dropped.
  const welcomeDiscount = intent?.first_ride_applied
    ? Math.max(0, +(totalDiscount - (promoApplied?.savings || 0)).toFixed(2))
    : 0;

  // WAITLIST riders should never see the checkout — backend gates
  // /pay/intent with require_approved_rider() and returns a structured 403
  // { code: "WAITLIST", message, access_status }. We handle it here at the
  // page level so the rider gets routed to /waitlist/status instead of an
  // ambiguous in-sheet error.
  const handleIntentFallback = (reason) => {
    console.error("[checkout-plan] intent fallback:", reason);
    const raw = String(reason || "");
    const upper = raw.toUpperCase();
    if (upper.includes("_WAITLIST_") || upper.includes("WAITLIST")) {
      toast.message("Your account is on the waitlist");
      nav("/waitlist/status", { replace: true });
      return;
    }
    if (upper.includes("_REJECTED_") || upper.includes("REJECTED")) {
      toast.error("Your account isn't authorized for booking. Contact support.");
      nav("/", { replace: true });
      return;
    }
    setIntentError(raw || "We couldn't reach Stripe. Please try again.");
  };

  const handleSuccess = () => {
    try { sessionStorage.removeItem(PLAN_STASH_KEY); } catch (_) { /* noop */ }
    nav("/checkout/success?activated=1", {
      state: {
        confirmed: {
          plan,
          dayStr,
          pickupLabel,
          returnLabel,
          seatsLabel,
          prefLabel,
          finalAmount,
          isWeekly,
          numDays,
        },
      },
    });
  };

  return (
    <motion.div
      className="min-h-[100dvh] text-white checkout-plan-frame"
      style={{
        background: "linear-gradient(180deg, rgba(10,17,40,0.98) 0%, rgba(7,13,31,1) 100%)",
      }}
      data-testid="checkout-plan"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
    >
      <MapFrostBackdrop level="full" zIndex={0} testId="checkout-frost-overlay" />
      <header
        className="sticky top-0 z-30 bg-[#0A1128]/70 backdrop-blur-md border-b border-white/10"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-11 flex items-center justify-between">
          <button
            type="button"
            onClick={() => nav(-1)}
            className="w-9 h-9 -ml-1 rounded-full flex items-center justify-center text-white/80 hover:bg-white/[0.08] transition-colors"
            aria-label="Back"
            data-testid="checkout-plan-back"
          >
            <ArrowLeft size={20} strokeWidth={1.8} />
          </button>
          <img src="/assets/link-full-logo.svg?v=13" alt="Link" className="h-5 w-auto" data-testid="checkout-plan-logo" />
          <div className="w-9" aria-hidden="true" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5 pb-28">
        {/* WEEKLY TRANSPORTATION COST — every dollar accounted for. */}
        <section className="rounded-3xl bg-white/[0.06] backdrop-blur-md border border-white/10 p-5 space-y-3 text-white" data-testid="summary-pricing">
          <SectionLabel text={isWeekly ? "Weekly transportation cost" : "Transportation cost"} />

          {/* Auto-applied welcome / first-ride badge */}
          {welcomeDiscount > 0 && (
            <div
              className="rounded-2xl bg-gradient-to-r from-[#ed6500]/20 to-[#ed6500]/10 border border-[#ed6500]/30 p-3.5 flex items-start gap-3"
              data-testid="auto-discount-badge"
            >
              <Sparkles size={18} className="text-[#ed6500] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-white">First Ride Discount Applied</div>
                <div className="text-[11px] text-white/70 mt-0.5">
                  Welcome to Link — you saved <span className="font-semibold text-[#ed6500] tabular-nums">${welcomeDiscount.toFixed(2)}</span> on your first transportation plan.
                </div>
              </div>
            </div>
          )}

          {/* Line items */}
          <LineItem label="Transportation cost" value={`$${baseAmount.toFixed(2)}`} testId="line-base" />
          {welcomeDiscount > 0 && (
            <LineItem label="First Ride Discount" value={`-$${welcomeDiscount.toFixed(2)}`} negative testId="line-first-ride" />
          )}
          {promoApplied && promoApplied.savings > 0 && (
            <LineItem label={promoApplied.code} value={`-$${promoApplied.savings.toFixed(2)}`} negative testId="line-promo" />
          )}
          <div className="border-t border-white/10 pt-3 flex items-baseline justify-between" data-testid="line-total-row">
            <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-white">
              {isWeekly ? "Weekly total" : "Today's total"}
            </div>
            <div className="font-serif text-[28px] text-[#ed6500] tabular-nums" data-testid="summary-total">
              ${finalAmount.toFixed(2)}
              {isWeekly && <span className="text-[12px] text-white/60 font-normal font-sans ml-1">/ week</span>}
            </div>
          </div>

          {/* Condensed reassurance — one body-text line, no header, no icons.
              Absorbs the old cancellation policy plus the useful bits of
              the retired "What happens next" card. */}
          <p
            className="text-[12px] text-white/65 pt-1 leading-relaxed"
            data-testid="checkout-reassurance"
          >
            {REASSURANCE_COPY}
          </p>
        </section>

        {/* Promo — collapsed text link inline; expands into an input on tap.
            Lives outside the pricing card so it doesn't visually compete
            with the total, but above payment so the discount lands before
            the charge. */}
        <div data-testid="promo-block">
          <PromoInput
            applied={promoApplied}
            onApply={(code) => setPromoCode(code)}
            onRemove={() => setPromoCode(null)}
            disabled={!routeId}
          />
        </div>

        {/* PAYMENT */}
        <section className="space-y-3" data-testid="summary-payment">
          <SectionLabel text="Payment method" />
          {creating || !routeId ? (
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] backdrop-blur-md p-8 flex items-center justify-center text-[13px] text-white/60" data-testid="checkout-plan-creating">
              Preparing payment…
            </div>
          ) : intentError ? (
            <div
              className="rounded-3xl bg-white/[0.06] backdrop-blur-md border border-[#ed6500]/40 p-6 space-y-3 text-white"
              data-testid="checkout-plan-intent-error"
              role="alert"
            >
              <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">
                We couldn&rsquo;t start checkout
              </div>
              <p className="text-[14px] text-white leading-relaxed">
                Something on our end blocked your payment from starting. Your transportation plan was saved &mdash; no charge was attempted.
              </p>
              <p className="text-[12px] text-white/60 leading-relaxed font-mono break-all" data-testid="checkout-plan-intent-error-reason">
                {intentError}
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setIntentError(null); setRouteId(plan?.routeId || routeId); window.location.reload(); }}
                  className="px-4 h-11 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors"
                  data-testid="checkout-plan-retry"
                >
                  Try again
                </button>
                <a
                  href="mailto:lamar@lefcoholdings.com?subject=Link%20checkout%20issue"
                  className="px-4 h-11 inline-flex items-center rounded-2xl bg-white/[0.08] border border-white/15 text-[11px] tracking-[0.22em] uppercase font-semibold text-white hover:border-[#ed6500] transition-colors"
                  data-testid="checkout-plan-support"
                >
                  Contact support
                </a>
              </div>
            </div>
          ) : (
            <StripeCheckoutSheet
              inline
              open={true}
              hidePromo
              externalPromoCode={promoCode}
              onIntentReady={setIntent}
              onClose={() => nav(-1)}
              onSuccess={handleSuccess}
              onFallback={handleIntentFallback}
              routeId={routeId}
              billingCycle={plan.billingCycle || "weekly"}
              amountDisplay={`$${finalAmount.toFixed(2)}`}
              subtitle={`${isWeekly ? "Weekly transportation" : "Single ride"} · ${dayStr || "Today"}`}
              ctaLabel="Confirm transportation plan"
            />
          )}
        </section>
      </main>
    </motion.div>
  );
}

/* ============================  bits  ============================ */

function SectionLabel({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-semibold text-white/60">
      {icon ? <span className="text-[#ed6500]">{icon}</span> : null}
      <span>{text}</span>
    </div>
  );
}

function LineItem({ label, value, negative, testId }) {
  return (
    <div className="flex items-baseline justify-between text-[13px]" data-testid={testId}>
      <div className={negative ? "text-white" : "text-white/85"}>{label}</div>
      <div className={`tabular-nums ${negative ? "text-[#3ecf8e] font-semibold" : "text-white"}`}>{value}</div>
    </div>
  );
}

/**
 * PromoInput — collapsed by default. The rider taps "Have a promo
 * code?" to reveal the input; APPLY is only fully-styled once text is
 * entered (before that it's disabled + reduced opacity). Once a promo
 * is applied (server-validated via the intent re-fetch), this flips to
 * a confirmation row with "savings" and an X to remove.
 */
function PromoInput({ applied, onApply, onRemove, disabled }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (applied?.code) {
    return (
      <div
        className="rounded-2xl bg-[#3ecf8e]/10 border border-[#3ecf8e]/30 px-4 py-3 flex items-center justify-between gap-3"
        data-testid="promo-applied"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Check size={16} strokeWidth={2.2} className="text-[#3ecf8e] shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-white" data-testid="promo-applied-code">{applied.code} Applied</div>
            {applied.savings > 0 && (
              <div className="text-[11px] text-white/65">You saved <span className="font-semibold text-[#3ecf8e] tabular-nums">${applied.savings.toFixed(2)}</span></div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setCode(""); setExpanded(false); onRemove?.(); }}
          className="w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:bg-white/10"
          aria-label="Remove promo code"
          data-testid="promo-remove"
        >
          <X size={14} strokeWidth={1.8} />
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e?.preventDefault?.();
    const v = code.trim().toUpperCase();
    if (!v || submitting) return;
    setSubmitting(true);
    try {
      onApply?.(v);
      setTimeout(() => setSubmitting(false), 6000);
    } catch (_) {
      setSubmitting(false);
    }
  };

  // Collapsed — a small text link that expands into the input on tap.
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        disabled={disabled}
        className="text-[13px] text-white/75 hover:text-[#ed6500] underline underline-offset-4 decoration-white/25 hover:decoration-[#ed6500] inline-flex items-center gap-1.5 disabled:opacity-40"
        data-testid="promo-expand"
      >
        <Tag size={12} strokeWidth={1.8} />
        Have a promo code?
      </button>
    );
  }

  const hasText = code.trim().length > 0;

  return (
    <form onSubmit={submit} className="flex items-stretch gap-2" data-testid="promo-input-form">
      <div className="flex-1 flex items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-md px-3.5 py-2.5">
        <Tag size={14} className="text-white/60 shrink-0" />
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Enter code"
          className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/40 uppercase tracking-wide"
          aria-label="Promo code"
          data-testid="promo-input"
          disabled={disabled}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
      </div>
      <button
        type="submit"
        disabled={!hasText || submitting || disabled}
        className={`px-5 rounded-2xl text-[11px] tracking-[0.22em] uppercase font-semibold transition-all ${
          hasText
            ? "bg-[#ed6500] hover:bg-[#ff7b1f] text-white"
            : "bg-[#0A1128]/10 text-[#0A1128]/40 cursor-not-allowed"
        }`}
        data-testid="promo-apply"
        aria-disabled={!hasText}
      >
        {submitting ? "…" : "Apply"}
      </button>
    </form>
  );
}
